import React, { forwardRef } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Spinner } from "./Spinner";

const SIZE_CLASS = {
  sm: "h-8 w-8",
  md: "h-10 w-10",
  lg: "h-12 w-12",
};

const ICON_SIZE = { sm: 16, md: 18, lg: 20 };

const VARIANT = {
  primary:
    "bg-frac-purple text-frac-white hover:bg-frac-purple-90 active:bg-frac-black",
  secondary:
    "bg-transparent text-frac-purple border border-frac-purple hover:bg-frac-purple-10 active:bg-frac-purple-30",
  ghost:
    "bg-transparent text-frac-purple hover:bg-frac-purple-10 active:bg-frac-purple-30",
  dark: "bg-frac-black text-frac-white hover:bg-surface-raised",
  "primary-on-dark":
    "bg-frac-violet text-frac-white hover:bg-[#8A1AE8] hover:shadow-glow",
  "secondary-on-dark":
    "bg-transparent text-frac-white border border-hairline hover:border-active-border hover:bg-[rgba(114,0,219,0.08)]",
};

/**
 * Square button for a single icon. `aria-label` is required.
 * `icon` is a lucide-react component (or any component that accepts { size, strokeWidth }).
 */
export const IconButton = forwardRef(function IconButton(
  {
    variant = "primary",
    size = "md",
    loading = false,
    disabled = false,
    as = "button",
    to,
    href,
    type = "button",
    icon: Icon,
    className,
    children,
    "aria-label": ariaLabel,
    ...rest
  },
  ref,
) {
  if (!ariaLabel && process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn("<IconButton> requires an aria-label.");
  }
  const classes = cn(
    "relative inline-flex items-center justify-center rounded-[10px] transition-all duration-fast ease-out",
    "disabled:opacity-45 disabled:cursor-not-allowed",
    SIZE_CLASS[size],
    VARIANT[variant],
    className,
  );
  const content = loading ? (
    <Spinner size="sm" />
  ) : Icon ? (
    <Icon size={ICON_SIZE[size]} strokeWidth={1.75} aria-hidden />
  ) : (
    children
  );

  if (as === "link" && to) {
    return (
      <Link ref={ref} to={to} aria-label={ariaLabel} className={classes} {...rest}>
        {content}
      </Link>
    );
  }
  if (as === "a" && href) {
    return (
      <a ref={ref} href={href} aria-label={ariaLabel} className={classes} {...rest}>
        {content}
      </a>
    );
  }
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
      className={classes}
      {...rest}
    >
      {content}
    </button>
  );
});

export default IconButton;
